/**
 * La matemática del gráfico de actividad, sin JSX.
 *
 * Aparte del componente para poder probarla con el ejecutor de Node, que no
 * carga archivos `.tsx`. Es también donde corresponde: cómo se rellenan los
 * tramos vacíos, cuál es el máximo «redondo» del eje y a qué punto cae el
 * cursor son reglas, no una vista, y se equivocan en silencio —un gráfico mal
 * escalado se lee igual de convincente que uno bien escalado—.
 */

/** Un tramo tal como lo entrega `/v1/serie`: los conteos llegan como texto. */
export interface PuntoCrudo {
  t: string;
  ejecuciones: string | number;
  ok: string | number;
  con_error: string | number;
  con_negocio: string | number;
  abandonados: string | number;
  p95_ms: number;
}

export interface Tramo {
  /** Inicio del tramo, en milisegundos desde 1970 (UTC). */
  t: number;
  ejecuciones: number;
  ok: number;
  /** Fallas de servicio o de la aplicación, sin los rechazos de negocio. */
  fallas: number;
  negocio: number;
  abandonados: number;
  /** 0 en un tramo sin ejecuciones. */
  p95: number;
}

/** ClickHouse entrega 'YYYY-MM-DD HH:MM:SS' o 'YYYY-MM-DD', siempre en UTC. */
export function aMilisUtc(t: string): number {
  const iso = t.includes(' ') ? `${t.replace(' ', 'T')}Z` : `${t}T00:00:00Z`;
  return new Date(iso).getTime();
}

/**
 * Una serie regular, con los tramos sin datos en cero.
 *
 * El servidor solo devuelve los tramos que tienen ejecuciones. Dibujarlos tal
 * cual uniría con una recta dos puntos separados por una hora sin tráfico, y
 * esa recta diría que hubo actividad a mitad de camino. Un tramo sin datos es
 * un cero, no un hueco que se interpola.
 */
export function rellenarTramos(
  puntos: readonly PuntoCrudo[],
  desdeMs: number,
  hastaMs: number,
  pasoSegundos: number
): Tramo[] {
  const paso = pasoSegundos * 1000;
  const porInicio = new Map<number, PuntoCrudo>();
  for (const p of puntos) porInicio.set(aMilisUtc(p.t), p);

  const tramos: Tramo[] = [];
  for (let t = Math.floor(desdeMs / paso) * paso; t <= hastaMs; t += paso) {
    const p = porInicio.get(t);
    tramos.push({
      t,
      ejecuciones: Number(p?.ejecuciones ?? 0),
      ok: Number(p?.ok ?? 0),
      fallas: Number(p?.con_error ?? 0),
      negocio: Number(p?.con_negocio ?? 0),
      abandonados: Number(p?.abandonados ?? 0),
      p95: Number(p?.p95_ms ?? 0),
    });
  }
  return tramos;
}

/** Porcentaje de `n` sobre `d`; 0 si no hay con qué dividir. */
export function tasa(n: number, d: number): number {
  return d > 0 ? (n / d) * 100 : 0;
}

/**
 * Cuánto cambió un valor respecto del anterior, en proporción.
 *
 * `null` cuando el anterior era cero: «subió infinito por ciento» no es una
 * cifra que alguien deba leer, y mostrar «+100%» sería inventar una escala.
 */
export function variacion(actual: number, previo: number): number | null {
  if (!Number.isFinite(actual) || !Number.isFinite(previo) || previo === 0) return null;
  return (actual - previo) / previo;
}

/**
 * Un máximo «redondo» para el eje, y las marcas entre 0 y él.
 *
 * El máximo de los datos tal cual da ejes con números como 137 o 83: legibles
 * como etiqueta, inútiles como regla. Se sube al siguiente 1, 2, 2,5, 5 o 10
 * de su orden de magnitud, que es donde caen las líneas de una regla.
 */
export function escalaDelEje(maximo: number, marcas = 4): { max: number; marcas: number[] } {
  if (!(maximo > 0)) return { max: 1, marcas: [0, 1] };
  const exponente = Math.floor(Math.log10(maximo));
  const base = 10 ** exponente;
  const fraccion = maximo / base;
  const paso = [1, 2, 2.5, 5, 10].find((p) => fraccion <= p) ?? 10;
  const max = paso * base;
  const salida: number[] = [];
  for (let i = 0; i <= marcas; i += 1) salida.push((max / marcas) * i);
  return { max, marcas: salida };
}

export interface Caja {
  x: number;
  y: number;
  ancho: number;
  alto: number;
}

/** El punto de cada tramo dentro de la caja de dibujo. */
export function puntos(valores: readonly number[], caja: Caja, max: number): Array<[number, number]> {
  const n = valores.length;
  return valores.map((v, i) => [
    n > 1 ? caja.x + (i / (n - 1)) * caja.ancho : caja.x + caja.ancho / 2,
    caja.y + caja.alto - (max > 0 ? Math.min(v / max, 1) : 0) * caja.alto,
  ]);
}

export function rutaDeLinea(valores: readonly number[], caja: Caja, max: number): string {
  return puntos(valores, caja, max)
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');
}

/** La línea cerrada contra la base, para rellenar debajo de ella. */
export function rutaDeArea(valores: readonly number[], caja: Caja, max: number): string {
  const p = puntos(valores, caja, max);
  if (p.length === 0) return '';
  const base = (caja.y + caja.alto).toFixed(1);
  const linea = p.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  return `${linea} L${p[p.length - 1]![0].toFixed(1)} ${base} L${p[0]![0].toFixed(1)} ${base} Z`;
}

/** A qué tramo corresponde una posición horizontal del cursor. */
export function indiceDeX(x: number, caja: Caja, cantidad: number): number {
  if (cantidad <= 1) return 0;
  const relativo = (x - caja.x) / caja.ancho;
  return Math.max(0, Math.min(cantidad - 1, Math.round(relativo * (cantidad - 1))));
}

/**
 * Qué marcas del eje horizontal mostrar.
 *
 * Una por cada tanto, no una por tramo: con 120 puntos las etiquetas se
 * montarían unas sobre otras. Siempre incluye el primero y deja el último
 * libre, que es «ahora» y se rotula aparte.
 */
export function indicesDeEtiquetas(cantidad: number, maximo: number): number[] {
  if (cantidad <= 1) return [0];
  const cuantas = Math.max(2, Math.min(maximo, cantidad));
  const salida = new Set<number>();
  for (let i = 0; i < cuantas; i += 1) salida.add(Math.round((i / (cuantas - 1)) * (cantidad - 1)));
  return [...salida].sort((a, b) => a - b);
}

/**
 * La etiqueta de un instante, con la precisión que pide el ancho del tramo.
 *
 * En hora local del navegador: un panel que muestra 17:00 UTC a alguien que
 * está en Santiago es un panel que miente por tres horas.
 */
export function etiquetaDeTramo(ms: number, pasoSegundos: number, ventanaDias: number): string {
  const fecha = new Date(ms);
  if (pasoSegundos >= 86400) {
    return fecha.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', timeZone: 'UTC' });
  }
  const hora = fecha.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (ventanaDias < 1.5) return hora;
  const dia = fecha.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' });
  return `${dia} ${hora}`;
}

/** Cifras grandes legibles: 1.240 en vez de 1240, 12,3 mil, 1,2 M. */
export function cifraCorta(valor: number): string {
  const abs = Math.abs(valor);
  if (abs >= 1_000_000) return `${(valor / 1_000_000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} M`;
  if (abs >= 10_000) return `${(valor / 1_000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} mil`;
  return valor.toLocaleString('es-CL', { maximumFractionDigits: 0 });
}

/** «186 ms», «1,9 s». */
export function duracionLegible(ms: number): string {
  if (!(ms > 0)) return '—';
  if (ms >= 1000) return `${(ms / 1000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} s`;
  return `${Math.round(ms)} ms`;
}
